import { Component } from '@angular/core';
import { Role } from '@app/models/account/role.model';
import { CompanyMFA } from '@app/models/company/settings/authentication/company-mfa.model';
import { CompanySSO } from '@app/models/company/settings/authentication/company-sso.model';
import { AuthService, SettingsService } from '@app/services';
import { Observable, ReplaySubject, forkJoin } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { SettingsMenuService } from '../../services/settings-menu.service';

type LoadingLevel = 'page' | 'card' | null;
@Component({
    templateUrl: './sign-in.template.html',
    styleUrls: ['./sign-in.style.scss'],
})
export class SignInSettingsView {
    private _companySSOSetting: ReplaySubject<CompanySSO | undefined> = new ReplaySubject();
    private _companyMFASetting: ReplaySubject<CompanyMFA | undefined> = new ReplaySubject();
    private _roles: ReplaySubject<Role[] | undefined> = new ReplaySubject();
    private _loadingLevel: LoadingLevel = null;

    constructor(
        public settingsMenuService: SettingsMenuService,
        private settingsService: SettingsService,
        private authService: AuthService
    ) {
        this.loadingLevel = 'card';
        this.loadRoles();
        this.loadSettings();

        forkJoin([this._companySSOSetting, this._companyMFASetting, this._roles]).subscribe(
            () => (this.loadingLevel = null)
        );
    }

    get companySSOSetting(): Observable<CompanySSO | undefined> {
        return this._companySSOSetting.asObservable();
    }

    get companyMFASetting(): Observable<CompanyMFA | undefined> {
        return this._companyMFASetting.asObservable();
    }

    get roles(): Observable<Role[] | undefined> {
        return this._roles.asObservable();
    }

    get loadingLevel(): LoadingLevel {
        if (!this.settingsMenuService.areDependenciesLoaded) {
            return 'page';
        }
        return this._loadingLevel;
    }

    set loadingLevel(level: LoadingLevel) {
        this._loadingLevel = level;
    }

    private loadSettings(): void {
        this.settingsService
            .onLoading()
            .pipe(
                takeWhile((isLoading) => isLoading) // unsubscribe when settingsService is done loading
            )
            .subscribe({
                complete: () => {
                    this.loadingLevel = null;
                    this._companySSOSetting.next(this.settingsService.getByName('companySSO'));
                    this._companySSOSetting.complete();
                    this._companyMFASetting.next(this.settingsService.getByName('companyMFA'));
                    this._companyMFASetting.complete();
                },
            });
    }

    private loadRoles(): void {
        Role.where('company_id', this.authService.company.id)
            .limit(0)
            .all()
            .then(([roles]) => this._roles.next(roles))
            .catch(() => this._roles.next(undefined))
            .finally(() => this._roles.complete());
    }
}
